// The admin design's Visual rules, as values. Every admin component takes its
// look from here (through adminTheme or directly), never from a hard-coded value.
// Source: design export "Visual rules" page + runway-app-tokens.css (XCUT-R7…R13).

const fallbackFonts = "Helvetica, Arial, sans-serif";
const inkSoft = "rgba(26, 60, 81, 0.72)";
const transparent = "transparent";
const noTracking = "normal";

const fontStack = (family: string) => `${family}, ${fallbackFonts}`;

export const adminFontFamily = {
  light: fontStack("Inter-Light"),
  regular: fontStack("Inter-Regular"),
  medium: fontStack("Inter-Medium"),
  mediumItalic: fontStack("Inter-MediumItalic"),
};

const color = {
  canvas: "#EEF1FB",
  surface: "#FFFFFF",
  warmGray: "#F5F5F0",
  cardGray: "#F2F2F2",
  periwinkle: "#AAC4F9",
  periwinkleHover: "#95B1F5",
  tealDark: "#3F757A",
  sage: "#DFEACD",
  navy: "#1A3C51",
  inkSoft,
  border: "#E1E5EC",
  errorStrong: "#B83A3A",
  warningStrong: "#C28522",
  successStrong: "#3F757A",
  disabledBackground: "#D9D9D9",
  disabledText: "#8C8F95",
  scrim: "rgba(26, 60, 81, 0.45)",
};

const statusTone = {
  processing: { background: "#DCE5F8", text: color.navy, border: transparent },
  moving: { background: "#D4E5E1", text: "#3F6063", border: transparent },
  done: { background: color.surface, text: color.tealDark, border: "#9FC2C5" },
  needsAttention: { background: "#FBF2DD", text: "#7A5A1F", border: transparent },
  error: { background: "#FCEBEA", text: "#9A2E2E", border: transparent },
  inactive: { background: color.cardGray, text: inkSoft, border: transparent },
};

const bannerTone = {
  success: { background: "#D4E5E1", border: "#9FC2C5", text: "#3F6063" },
  info: { background: "#DCE5F8", border: "#A6BDF2", text: color.navy },
  warning: { background: "#FBF2DD", border: "#EFD9A2", text: "#7A5A1F" },
  error: { background: "#FCEBEA", border: "#E8B4B0", text: "#9A2E2E" },
};

// The design's field error icon and the marker of an unselected radio card.
const fieldTone = {
  errorIcon: "#C44545",
  markerMuted: "#C0C4D0",
};

// The design system's focus ring, `--rh-app-focus-ring` (XCUT-R38); on the navy notice
// strip it turns white to keep its contrast (XCUT-R39).
const focusRingWidth = 3;
const focusRingOffset = 2;

const focusRing = { width: focusRingWidth, offset: focusRingOffset, color: color.tealDark };
const focusRingOnNavy = { width: focusRingWidth, offset: focusRingOffset, color: color.surface };

const typeScale = {
  pageTitle: {
    fontFamily: adminFontFamily.light,
    fontSize: "26px",
    lineHeight: 1.2,
    letterSpacing: noTracking,
  },
  keyFigure: {
    fontFamily: adminFontFamily.light,
    fontSize: "36px",
    lineHeight: 1.2,
    letterSpacing: noTracking,
  },
  cardHeading: {
    fontFamily: adminFontFamily.medium,
    fontSize: "18px",
    lineHeight: 1.3,
    letterSpacing: noTracking,
  },
  body: {
    fontFamily: adminFontFamily.regular,
    fontSize: "14px",
    lineHeight: 1.5,
    letterSpacing: noTracking,
  },
  labelValue: {
    fontFamily: adminFontFamily.medium,
    fontSize: "14px",
    lineHeight: 1.5,
    letterSpacing: noTracking,
  },
  eyebrow: {
    fontFamily: adminFontFamily.medium,
    fontSize: "12px",
    letterSpacing: "0.1em",
    textTransform: "uppercase" as const,
  },
  meta: {
    fontFamily: adminFontFamily.regular,
    fontSize: "13px",
    lineHeight: 1.5,
    letterSpacing: noTracking,
  },
  medication: {
    fontFamily: adminFontFamily.mediumItalic,
    fontStyle: "italic" as const,
    fontSize: "14px",
  },
};

export const adminTokens = {
  color,
  focusRing,
  focusRingOnNavy,
  statusTone,
  bannerTone,
  fieldTone,
  typeScale,
  spacing: [4, 8, 12, 16, 20, 24, 32],
  radius: { card: 20, row: 12, input: 8, pill: 9999 },
  density: {
    controlHeight: 36,
    dialogControlHeight: 40,
    tableRowHeight: 44,
    minimumHitTarget: 32,
  },
  shadow: {
    card: "0 2px 8px rgba(26, 60, 81, 0.06)",
    raised: "0 8px 24px rgba(26, 60, 81, 0.08)",
  },
};
